/* ==========================================================================
   Rook UI Core — Modal Lightbox Controller
   Web Component: <rk-modal-element>

   Generic fullscreen lightbox container. Accepts any content by default
   (type: 'custom') and provides convenience presets for media types
   (image, video, youtube, vimeo, html). Includes custom Instagram-style
   video controls for hosted video playback.
   ========================================================================== */

if (!customElements.get('rk-modal-element')) {
    class RkModalElement extends HTMLElement {
        constructor() {
            super();
            this.modal = null;
            this.content = null;
            this.previousFocus = null;
            this._raf = null;

            this._onKeyDown = this._onKeyDown.bind(this);
        }

        connectedCallback() {
            this.modal = this.querySelector('.rk-modal');
            this.content = this.querySelector('.rk-modal__content');

            if (!this.modal) return;

            this._bindEvents();
            this._setupOpenTriggers();
            this._setupVideoControls();
        }

        disconnectedCallback() {
            if (this._raf) cancelAnimationFrame(this._raf);
        }

        /* ------------------------------------------------------------------ */
        /*  Events                                                            */
        /* ------------------------------------------------------------------ */

        _bindEvents() {
            this.querySelectorAll('[data-action="close"]').forEach((el) => {
                el.addEventListener('click', () => this.close());
            });
        }

        _setupOpenTriggers() {
            const modalId = this.dataset.modalId || this.modal?.id;
            if (!modalId) return;

            document.querySelectorAll(`[data-modal-open="${modalId}"]`).forEach((trigger) => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.open();
                });
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Custom video controls (Instagram-style)                           */
        /* ------------------------------------------------------------------ */

        _setupVideoControls() {
            const video = this.querySelector('.rk-modal__video');
            if (!video) return;

            this._video = video;
            this._controls = this.querySelector('.rk-modal__controls');
            this._progressBar = this.querySelector('.rk-modal__progress-bar');
            this._progressTrack = this.querySelector('[data-ctrl="progress"]');
            this._timeDisplay = this.querySelector('[data-ctrl="time"]');
            this._playPauseBtn = this.querySelector('[data-ctrl="play-pause"]');
            this._muteBtn = this.querySelector('[data-ctrl="mute"]');
            this._tapOverlay = this.querySelector('[data-ctrl="tap"]');

            // Play/pause button
            if (this._playPauseBtn) {
                this._playPauseBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._togglePlay();
                });
            }

            // Mute button
            if (this._muteBtn) {
                this._muteBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._toggleMute();
                });
            }

            // Tap overlay — toggle play/pause
            if (this._tapOverlay) {
                this._tapOverlay.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._togglePlay();
                });
            }

            // Progress bar click — seek
            if (this._progressTrack) {
                this._progressTrack.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this._seek(e);
                });
            }

            // Sync UI with video state
            video.addEventListener('play', () => this._syncPlayState());
            video.addEventListener('pause', () => this._syncPlayState());
            video.addEventListener('volumechange', () => this._syncMuteState());
        }

        _togglePlay() {
            if (!this._video) return;
            if (this._video.paused) {
                this._video.play().catch(() => {});
            } else {
                this._video.pause();
            }
        }

        _toggleMute() {
            if (!this._video) return;
            this._video.muted = !this._video.muted;
        }

        _seek(e) {
            if (!this._video || !this._progressTrack) return;
            const rect = this._progressTrack.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            this._video.currentTime = ratio * this._video.duration;
        }

        _syncPlayState() {
            if (!this._video || !this._controls) return;
            const playing = !this._video.paused;
            this._controls.classList.toggle('is-playing', playing);
            this._controls.classList.toggle('is-paused', !playing);

            if (playing) {
                this._startProgressLoop();
            } else {
                this._stopProgressLoop();
            }
        }

        _syncMuteState() {
            if (!this._video || !this._controls) return;
            this._controls.classList.toggle('is-muted', this._video.muted);
            this._controls.classList.toggle('is-unmuted', !this._video.muted);
        }

        _startProgressLoop() {
            const update = () => {
                if (!this._video || this._video.paused) return;

                const pct = (this._video.currentTime / this._video.duration) * 100 || 0;
                if (this._progressBar) {
                    this._progressBar.style.width = pct + '%';
                }

                if (this._timeDisplay) {
                    this._timeDisplay.textContent = this._formatTime(this._video.currentTime);
                }

                this._raf = requestAnimationFrame(update);
            };
            this._raf = requestAnimationFrame(update);
        }

        _stopProgressLoop() {
            if (this._raf) {
                cancelAnimationFrame(this._raf);
                this._raf = null;
            }
        }

        _formatTime(seconds) {
            const m = Math.floor(seconds / 60);
            const s = Math.floor(seconds % 60);
            return m + ':' + (s < 10 ? '0' : '') + s;
        }

        /* ------------------------------------------------------------------ */
        /*  Public API                                                        */
        /* ------------------------------------------------------------------ */

        isOpen() {
            return this.modal?.classList.contains('rk-modal--active');
        }

        open() {
            if (!this.modal || this.isOpen()) return;

            this.previousFocus = document.activeElement;
            this.modal.classList.add('rk-modal--active');
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', this._onKeyDown);

            // Load lazy iframes (YouTube/Vimeo)
            this._loadIframes();

            // Autoplay hosted video (muted)
            this._playMedia();

            // Sync initial state
            if (this._video) {
                this._syncPlayState();
                this._syncMuteState();
            }

            // Focus close button
            requestAnimationFrame(() => {
                const closeBtn = this.querySelector('.rk-modal__close');
                if (closeBtn) closeBtn.focus();
            });

            this.dispatchEvent(
                new CustomEvent('rk:modal:open', {
                    bubbles: true,
                    detail: { id: this.modal.id, type: this.dataset.type },
                })
            );
        }

        close() {
            if (!this.modal || !this.isOpen()) return;

            this.modal.classList.remove('rk-modal--active');
            document.body.style.overflow = '';
            document.removeEventListener('keydown', this._onKeyDown);

            // Stop all media
            this._stopMedia();
            this._stopProgressLoop();

            if (this.previousFocus) {
                this.previousFocus.focus();
                this.previousFocus = null;
            }

            this.dispatchEvent(
                new CustomEvent('rk:modal:close', {
                    bubbles: true,
                    detail: { id: this.modal.id, type: this.dataset.type },
                })
            );
        }

        /* ------------------------------------------------------------------ */
        /*  Media helpers                                                     */
        /* ------------------------------------------------------------------ */

        _loadIframes() {
            this.querySelectorAll('iframe[data-src]').forEach((iframe) => {
                if (!iframe.src || iframe.src === 'about:blank') {
                    iframe.src = iframe.dataset.src;
                }
            });
        }

        _playMedia() {
            const video = this.querySelector('video[data-autoplay]');
            if (video) {
                video.muted = true;
                video.currentTime = 0;
                video.play().catch(() => {
                    /* autoplay blocked — silent fail */
                });
            }
        }

        _stopMedia() {
            // Pause hosted videos
            this.querySelectorAll('video').forEach((v) => {
                v.pause();
                v.currentTime = 0;
            });

            // Clear iframe src to stop YouTube/Vimeo playback
            this.querySelectorAll('iframe').forEach((iframe) => {
                if (iframe.dataset.src) {
                    iframe.src = 'about:blank';
                }
            });
        }

        /* ------------------------------------------------------------------ */
        /*  Keyboard (ESC + Space for play/pause)                             */
        /* ------------------------------------------------------------------ */

        _onKeyDown(e) {
            if (!this.isOpen()) return;

            if (e.key === 'Escape') {
                e.preventDefault();
                this.close();
                return;
            }

            // Space toggles play/pause on video
            if (e.key === ' ' && this._video) {
                e.preventDefault();
                this._togglePlay();
                return;
            }

            // M toggles mute on video
            if (e.key === 'm' && this._video) {
                e.preventDefault();
                this._toggleMute();
            }
        }
    }

    customElements.define('rk-modal-element', RkModalElement);
}
